"use client";

import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;
const COLORS = ["#020637", "#7782F8", "#99A1FA", "#BBC1FB"];

export default function TopServicesStats() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers = buildHeaders();

        const res = await fetch(`${API_URL}/analytics/best-services/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        const formatted =
          data?.besd_services?.map((s) => ({
            name: s.name,
            percent: parseFloat(s.percentage) || 0,
          })) ?? [];

        setData(formatted);
        setClients(data?.clients || {});
      } catch (err) {
        console.error("❌ Failed to fetch Top Services Stats:", err);
        setError(t("home.errorLoadStats"));
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [token, navigate, t]);

  return (
    <div
      className="flex flex-col justify-start bg-white rounded-2xl shadow-md p-6 h-auto sm:h-[343px] gap-[10px]"
      style={{ paddingTop: "15px" }}
    >
      <h2 className="text-[#302B2B] text-2xl font-bold">
        {t("home.topServicesStatsTitle")}
      </h2>

      {loading ? (
        <p className="text-neutral-400 text-sm text-center">
          {t("home.loadingStats")}
        </p>
      ) : error ? (
        <p className="text-red-500 text-sm italic text-center">{error}</p>
      ) : data.length === 0 ? (
        <p className="text-[#9A99AB] text-sm text-center">
          {t("home.noServiceData")}
        </p>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* ==== Donut Chart ==== */}
          <div className="w-full max-w-[270px] aspect-square h-auto sm:w-[270px] sm:h-[270px] mx-auto relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="percent"
                  paddingAngle={2}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke="none"
                    />
                  ))}
                </Pie>

                <Tooltip
                  cursor={false}
                  content={({ active, payload }) =>
                    active && payload && payload.length ? (
                      <div className="bg-white border border-[#F6F6F6] rounded-lg px-3 py-1 text-sm shadow">
                        <span className="text-[#302B2B] font-medium">
                          {payload[0].name}
                        </span>{" "}
                        <span className="text-[#020637]">
                          {payload[0].value}%
                        </span>
                      </div>
                    ) : null
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col gap-5">
            {data.map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 w-[205px]"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-[12px] h-[4px] rounded-md"
                    style={{ background: COLORS[i % COLORS.length] }}
                  ></div>
                  <span className="text-[#302B2B] text-[18px] font-normal">
                    {item.name}
                  </span>
                </div>
                <span className="text-[#9A99AB] text-[16px]">
                  {item.percent}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
